import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { FileDown, CheckCircle2, ArrowRight } from "lucide-react";
import api from "../lib/api";
import { track } from "../lib/track";
import { Seo, breadcrumbSchema } from "../components/site/Seo";
import { ContactActions } from "../components/site/ContactActions";

export const COMPETENCIES = ["Industrial & MRO supplies", "Equipment", "Safety / PPE", "Facility supplies",
  "IT equipment", "Medical supplies", "Logistics & delivery coordination", "Strategic sourcing & procurement support"];
const DIFFERENTIATORS = [
  ["Source Smarter", "Qualified suppliers identified for the products and equipment your agency needs."],
  ["Compare Options", "Pricing, availability, lead times, and logistics evaluated before purchase."],
  ["Simplify Procurement", "One procurement partner instead of multiple sourcing conversations."],
  ["Coordinate Delivery", "Movement of products coordinated from supplier to destination."],
];
export const FEDERAL_CAPS = ["NSN & Part Number Sourcing", "Industrial & MRO Components", "Electrical & Electronic Components", "Equipment & Replacement Parts",
  "Safety & Facility Supplies", "Supplier Identification & Verification", "Competitive Quote Development", "Lead-Time & Availability Verification", "Logistics & Delivery Coordination"];
export const FEDERAL_SUPPORT_TEXT = "PGPS supports federal agencies, defense contractors and prime contractors by identifying commercial suppliers, obtaining pricing and availability information, comparing sourcing options and coordinating fulfillment and delivery.";
const EXPECTATIONS = ["Responsive communication", "Structured sourcing", "Competitive supplier options",
  "Clear procurement information", "Supplier coordination", "Delivery coordination"];

export function useGovCredentials() {
  const [creds, setCreds] = useState(null);
  useEffect(() => {
    api.get("/settings/public").then(({ data }) => setCreds(data?.gov_credentials || {})).catch(() => setCreds({}));
  }, []);
  return creds;
}

export function credentialRows(creds) {
  if (!creds) return [];
  return [
    ["Legal Business Name", creds.legal_name],
    ["SAM.gov Registration", creds.sam_status],
    ["UEI", creds.uei],
    ["CAGE Code", creds.cage_code],
    ["NAICS", (creds.naics || []).join(", ")],
    ["NIGP", (creds.nigp || []).join(", ")],
    ["Certifications", (creds.certifications || []).join(", ")],
    ["Contract Vehicles", (creds.contract_vehicles || []).join(", ")],
  ].filter(([, v]) => v && String(v).trim());
}

export function CapabilityDownload({ className = "" }) {
  return (
    <a href={`${process.env.REACT_APP_BACKEND_URL}/api/capability-statement.pdf`} data-testid="capability-statement-download"
      target="_blank" rel="noopener noreferrer" onClick={() => track("capability_statement_download", {})}
      className={`inline-flex items-center gap-2 btn-primary !py-3 !px-5 text-[13px] ${className}`}>
      <FileDown size={16} /> Download Capability Statement (PDF)
    </a>
  );
}

function Block({ title, children, testid }) {
  return (
    <section data-testid={testid} className="border-t border-slate-200 pt-6 mt-6 first:border-0 first:pt-0 first:mt-0">
      <h2 className="font-[Poppins] font-bold text-[16px] md:text-lg text-[var(--pg-navy)]">{title}</h2>
      <div className="mt-3">{children}</div>
    </section>
  );
}

export default function CapabilityStatementPage() {
  const creds = useGovCredentials();
  const rows = credentialRows(creds);
  const past = creds?.past_performance || [];
  return (
    <div data-testid="capability-statement-page">
      <Seo route="/capability-statement"
        jsonLd={breadcrumbSchema([{ name: "Home", path: "/" }, { name: "Government Contracting", path: "/government" }, { name: "Capability Statement", path: "/capability-statement" }])} />
      <section className="pt-[130px] pb-12 hero-bg">
        <div className="pg-container max-w-4xl">
          <span className="eyebrow" style={{ color: "#7EC4FF" }}>For Contracting &amp; Purchasing Officers</span>
          <h1 className="mt-5 text-[36px] md:text-[50px] font-extrabold leading-[1.06] text-white">Capability Statement</h1>
          <p className="mt-5 text-[17px] text-white/80 max-w-2xl leading-relaxed">
            Patrick Global Procurement Services · Houston, Texas. This page is the accessible web version of our one-page capability statement.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <CapabilityDownload />
            <Link to="/government" data-testid="capability-back-gov" className="inline-flex items-center gap-2 font-[Poppins] font-semibold px-5 py-3 rounded-full border border-white/35 text-white hover:bg-white hover:text-[var(--pg-navy)] transition-colors text-[13px]">
              Government Contracting page <ArrowRight size={15} />
            </Link>
          </div>
        </div>
      </section>
      <section className="py-14 md:py-20 bg-[var(--pg-bg)]">
        <div className="pg-container max-w-4xl">
          <article className="bg-white border border-slate-200 rounded-2xl p-7 md:p-10" data-testid="capability-statement-body">
            <Block title="Company Overview" testid="cap-overview">
              <p className="text-[15px] text-[var(--pg-ink-soft)] leading-relaxed">
                Patrick Global helps organizations source products, equipment, supplies, and procurement requirements through
                structured sourcing, supplier coordination, procurement management, and logistics support. We support federal,
                state, and municipal agencies, government contractors, prime contractors, and subcontracting opportunities with
                responsive, documented sourcing.
              </p>
            </Block>
            <Block title="Core Competencies" testid="cap-competencies">
              <ul className="grid sm:grid-cols-2 gap-2">
                {COMPETENCIES.map((c) => <li key={c} className="flex items-start gap-2 text-[14.5px] text-[var(--pg-navy)]"><CheckCircle2 size={17} className="text-[var(--pg-accent)] mt-0.5 flex-shrink-0" />{c}</li>)}
              </ul>
            </Block>
            <Block title="Federal Supply Capabilities" testid="cap-federal">
              <ul className="grid sm:grid-cols-2 gap-2">
                {FEDERAL_CAPS.map((c) => <li key={c} className="flex items-start gap-2 text-[14.5px] text-[var(--pg-navy)]"><CheckCircle2 size={17} className="text-[var(--pg-accent)] mt-0.5 flex-shrink-0" />{c}</li>)}
              </ul>
              <h3 className="mt-5 font-[Poppins] font-semibold text-[14.5px] text-[var(--pg-navy)]">Federal Procurement Support</h3>
              <p className="mt-1.5 text-[14.5px] text-[var(--pg-ink-soft)] leading-relaxed">{FEDERAL_SUPPORT_TEXT}</p>
            </Block>
            <Block title="Differentiators" testid="cap-differentiators">
              <ul className="space-y-2">
                {DIFFERENTIATORS.map(([h, b]) => <li key={h} className="text-[14.5px] text-[var(--pg-ink-soft)]"><strong className="text-[var(--pg-navy)]">{h}</strong> — {b}</li>)}
              </ul>
            </Block>
            {rows.length > 0 && (
              <Block title="Registrations & Codes" testid="cap-credentials">
                <dl className="border border-slate-200 rounded-xl divide-y divide-slate-100">
                  {rows.map(([k, v]) => (
                    <div key={k} className="flex flex-wrap gap-2 justify-between px-4 py-3">
                      <dt className="font-[Poppins] font-semibold text-[13.5px] text-[var(--pg-ink-soft)]">{k}</dt>
                      <dd className="text-[14px] font-medium text-[var(--pg-navy)] text-right max-w-[70%]">{v}</dd>
                    </div>
                  ))}
                </dl>
              </Block>
            )}
            {past.length > 0 && (
              <Block title="Past Performance" testid="cap-past-performance">
                <ul className="list-disc pl-5 space-y-1 text-[14.5px] text-[var(--pg-ink-soft)]">{past.map((p) => <li key={p}>{p}</li>)}</ul>
              </Block>
            )}
            <Block title="Global Service Area & Delivery" testid="cap-service-area">
              <p className="text-[14.5px] text-[var(--pg-ink-soft)] leading-relaxed">
                Headquartered in Houston, Texas, we support buyers across the United States and coordinate sourcing, supplier
                shipments, and freight to agency, contractor, and partner locations worldwide.
              </p>
            </Block>
            <Block title="What Agencies Can Expect" testid="cap-expectations">
              <ul className="grid sm:grid-cols-2 gap-2">
                {EXPECTATIONS.map((c) => <li key={c} className="flex items-start gap-2 text-[14.5px] text-[var(--pg-navy)]"><CheckCircle2 size={17} className="text-[var(--pg-accent)] mt-0.5 flex-shrink-0" />{c}</li>)}
              </ul>
            </Block>
            <Block title="Contact" testid="cap-contact">
              <address className="not-italic text-[14.5px] text-[var(--pg-ink-soft)] leading-relaxed">
                Patrick Global Procurement Services<br />8700 Commerce Park Drive, Suite 238, Houston, TX 77036<br />
                <a href="tel:+12819019914" className="text-[var(--pg-navy)] font-semibold hover:text-[var(--pg-accent)]" onClick={() => track("phone_click", { source: "capability_page" })}>281-901-9914</a> ·{" "}
                <a href="mailto:info@pgpsusa.com" className="text-[var(--pg-navy)] font-semibold hover:text-[var(--pg-accent)]" onClick={() => track("email_click", { source: "capability_page" })}>info@pgpsusa.com</a> · pgpsusa.com
              </address>
              <ContactActions source="capability_page" dark={false} className="mt-5" />
            </Block>
          </article>
        </div>
      </section>
    </div>
  );
}
